import { useEffect, useState } from "react";
import { loadTheme, isThemeLoaded } from "../utils/theme";

/**
 * Optimized hook to load and manage brand themes
 * - Uses cache to avoid reloading
 * - Non-blocking: doesn't show loading state if theme is already loaded
 * - Prevents unnecessary re-renders
 */
export const useTheme = (brand?: string) => {
  const [isLoading, setIsLoading] = useState(() => {
    // Check if theme is already loaded on mount
    const themeName = brand || "default";
    return !isThemeLoaded(themeName);
  });

  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const themeName = brand || "default";

    // If already loaded, no need to show loading
    if (isThemeLoaded(themeName)) {
      setIsLoading(false);
      return;
    }

    let isMounted = true;

    const loadBrandTheme = async () => {
      try {
        setIsLoading(true);
        setError(null);

        await loadTheme(brand);

        if (isMounted) {
          setIsLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setError(
            err instanceof Error ? err : new Error("Failed to load theme"),
          );
          setIsLoading(false);
        }
      }
    };

    loadBrandTheme();

    return () => {
      isMounted = false;
    };
  }, [brand]);

  return {
    isLoading,
    error,
  };
};
